import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar } from 'lucide-react';
import { useCMS, consultantField, activeConsultants } from '../context/CMSContext';
import { useSeo, breadcrumb } from '../lib/seo';
import { resolveConsultantImage } from '../lib/images';

const ConsultantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cms, lang } = useCMS();

  // CMS data is available synchronously (bundled fallback + live overlay),
  // so the lookup needs no loading state.
  const consultant = activeConsultants(cms).find((c) => String(c.id) === String(id));

  const cName = consultant ? consultantField(consultant, 'name', lang) : '';
  const cTitle = consultant ? consultantField(consultant, 'title', lang) : '';
  const cDesc = consultant ? consultantField(consultant, 'description', lang) : '';

  useSeo({
    title: cName ? `${cName}｜${cTitle}｜${cms.site.orgNameTc}` : `顧問團隊｜${cms.site.orgNameTc}`,
    description: cName
      ? `${cName} 是 ${cms.site.orgNameTc} (${cms.site.orgNameEn}) 的${cTitle}。${cDesc.slice(0, 140)}`
      : `${cms.site.orgNameTc} 資深顧問團隊，專精上市、併購與資本市場。`,
    path: id ? `/consultants/${id}` : '/consultants',
    ogType: 'profile',
    jsonLd: consultant
      ? [
          breadcrumb([
            { name: '首頁', path: '/' },
            { name: '顧問團隊', path: '/consultants' },
            { name: cName, path: `/consultants/${id}` },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: cName,
            jobTitle: cTitle,
            description: cDesc,
            worksFor: {
              '@type': 'ProfessionalService',
              name: cms.site.orgNameTc,
              alternateName: cms.site.orgNameEn,
            },
          },
        ]
      : undefined,
  });

  if (!consultant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-24 px-4">
        <p className="text-gray-600 mb-6">找不到此顧問頁面。</p>
        <Link
          to="/consultants"
          className="px-6 py-3 bg-navy-900 text-white rounded hover:bg-navy-800 transition-colors"
        >
          {t('nav.consultants')}
        </Link>
      </div>
    );
  }

  const imageSrc = resolveConsultantImage(consultant.imageUri);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-navy-900 mb-8 transition-colors">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Team
        </button>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-gray-200 flex items-center justify-center bg-opacity-30">
              <img
                src={imageSrc}
                alt={cName}
                className="w-full object-cover object-top h-[800px] md:h-[400px]"
              />
            </div>
            <div className="p-8 md:w-2/3">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-navy-900 font-serif mb-2">{cName}</h1>
                <p className="text-xl text-gold-500 font-medium">{cTitle}</p>
              </div>

              <div className="prose max-w-none text-gray-600 mb-8 whitespace-pre-wrap">
                {cDesc}
              </div>

              <div className="flex gap-4">
                <Link to="/contact" className="flex items-center justify-center px-6 py-3 bg-navy-900 text-white rounded hover:bg-navy-800 transition-colors">
                  <Calendar className="h-5 w-5 mr-2" />
                  {t('common.scheduleConsultation')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantDetail;
